import '../sharpen/sharpen-legal-disclaimer';
import './sharpen-upsell-modal-cart-product';
import mapMagentoProductsToCardProducts from '../cart/cart-list/mapping/cart-list.mapping';
import sharpenUpsellStyles from './styles/sharpen-upsell-styles';
import store, { waitForCart } from '../../store/store';
import { ActionStatus } from '../../common/constants/action-status';
import { AnalyticsOrderFlowTypes } from '../../common/utils/analytics-util';
import { BaseComponent } from '../base-component/base-component';
import { BusinessUnit } from '../checkout/shop-checkout.interface';
import { Modal } from '../modal/modal';
import { ModalButton, ModalSize } from '../modal/interfaces/modal.interface';
import { OrderType } from '../../common/constants/order-types';
import { ProductCardData } from '../product-card/product-card-display';
import {
  SHARPEN_TILE_TITLE,
  SHARPEN_TUNNEL_DESCRIPTION,
  SharpenPageType,
} from './constants/sharpen-page-type.constants';
import { SharpenSearchServiceData } from './interfaces/sharpen-search-service-product-data.interface';
import { SharpenUpsellAnalytics } from './sharpen-upsell.analytics';
import { StoreState } from '../../store/store.interface';
import { SubscriptionProductData } from '../subscription/add-to-cart/subscription-add-to-cart';
import { addToCart } from '../cart/shop-cart/thunks/add-to-cart.thunk';
import { addToCartUrlContext, getCheckoutUrl } from '../../common/utils/cart-util';
import { booleanConverter } from '../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { fetchAllSharpenProducts } from './thunks/sharpen-search-service-data.thunk';
import { getCartContext, getCartStatus, getProductsFromCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { getImageSrcUrl } from '../../common/utils/product-util';
import {
  getSharpenSearchServiceProductData,
  waitForSharpenSearchServiceProduct,
} from './store/sharpen-product-data.selector';
import { getShopCart } from '../cart/shop-cart/thunks/get-shop-cart.thunk';
import {
  getSortedSharpenOffersByDuration,
  isCartSuccess,
  isSharpenPlusBundle,
  isSharpenSearchServiceProductSuccess,
} from './utils/sharpen-upsell.utils';
import { html, nothing, TemplateResult } from 'lit';
import { saveCartLocalStorageObject } from '../search/utils/search.util';
import { setSelectedSharpenUpsellData } from './store/sharpen-product-data.store';
import { getApiErrorMessage } from '../../common/utils/error-message.util';

@customElement('mhecomm-sharpen-upsell-modal')
export class SharpenUpsellModal extends connect(store)(BaseComponent) {
  static styles = [sharpenUpsellStyles];

  @property()
  contentTitle = '';

  @property()
  contentDescription = '';

  @property()
  heProgramSourceId?: string;

  @property()
  modalHeader = 'Recommended by McGraw Hill';

  @property()
  primaryButtonLabel = 'Add Sharpen';

  @property()
  primaryButtonAriaLabel = 'Add Sharpen Plus to order and begin checkout';

  @property()
  secondaryButtonLabel = 'Decline Sharpen';

  @property()
  secondaryButtonAriaLabel = 'Decline Sharpen Plus and begin checkout';

  @property({ type: Array })
  sharpenIsbnList?: string[];

  @property()
  sharpenOfferDescription = SHARPEN_TUNNEL_DESCRIPTION;

  @property()
  sharpenOfferTitle = SHARPEN_TILE_TITLE;

  @property({ converter: booleanConverter })
  showAddedToCartProduct = false;

  @property({ converter: booleanConverter })
  showContentTitleAndDescription = false;

  @property()
  store?: string;

  @state()
  private addToCartFailed = false;

  @state()
  private connectProduct?: ProductCardData;

  @state()
  private isSearchServiceCallFailed = false;

  @state()
  private sharpenProduct?: SubscriptionProductData;

  @state()
  private sharpenSearchServiceProductData?: SharpenSearchServiceData;

  private modal?: Modal;

  private primaryButton?: ModalButton;

  private secondaryButton?: ModalButton;

  connectedCallback(): void {
    super.connectedCallback();
    this.primaryButton = {
      ariaLabel: this.primaryButtonAriaLabel,
      label: this.primaryButtonLabel,
      showSpinner: true,
    };
    this.secondaryButton = {
      ariaLabel: this.secondaryButtonAriaLabel,
      label: this.secondaryButtonLabel,
      showSpinner: true,
    };
  }

  /**
   * Tries to load the existing cart to determine if it has a product eligible for upsell.
   * If it is the case, it tries to load the Sharpen offers.
   *
   * @param _changedProperties
   */
  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
    super.firstUpdated(_changedProperties);
    await store.dispatch(getShopCart({ store: this.store }));
    await waitForCart();

    const state = store.getState() as StoreState;
    if (isCartSuccess(state) && isSharpenPlusBundle(state, this.sharpenIsbnList)) {
      await this.loadModalDataFromCart();
    }
  }

  /**
   * Tries to load the Sharpen offers from the search service.
   * If it worked, it opens the modal.
   */
  private async loadModalDataFromCart(): Promise<void> {
    await store.dispatch(fetchAllSharpenProducts());
    const state = store.getState() as StoreState;
    await waitForSharpenSearchServiceProduct();

    if (isSharpenSearchServiceProductSuccess(state)) {
      this.mapSharpenProductData(state);
      this.connectProduct =
        mapMagentoProductsToCardProducts(getProductsFromCart(state) ?? [], this.store)[0] ?? undefined;
      this.triggerModal();
      return;
    }

    this.isSearchServiceCallFailed = true;
  }

  renderComp(): TemplateResult | typeof nothing {
    if (!this.addToCartFailed) {
      return nothing;
    }

    return html`
      <mhecomm-toast
        type="primary"
        header="Error Adding Item to Cart"
        .body=${getApiErrorMessage({ bold: false })}
        ?autoHide="${false}"
        @toast-close="${() => (this.addToCartFailed = false)}">
      </mhecomm-toast>
    `;
  }

  /**
   * Triggers the Sharpen upsell modal if the state allows for it.
   */
  private triggerModal(): void {
    if (this.modal || !this.connectProduct || !this.sharpenProduct) {
      return;
    }

    this.modal = new Modal({
      a11y: { announceBody: true },
      body: { template: this.renderModalContent() },
      header: this.modalHeader,
      isHeaderTextBold: true,
      modalContext: { componentRef: 'sharpen-upsell' },
      modalSize: ModalSize.LARGE,
      primaryButton: this.primaryButton,
      secondaryButton: this.secondaryButton,
      showClosingCross: true,
    });

    this.modal.addTrackedEventListener(this.modal, 'mhecomm-modal-confirmed', this.onConfirm.bind(this));
    this.modal.addTrackedEventListener(this.modal, 'sharpen-upsell-modal-close', this.onClose.bind(this));
    this.modal.addTrackedEventListener(this.modal, 'sharpen-upsell-modal-secondary', this.onDecline.bind(this));
    this.modal.open();
  }

  private renderModalContent(): TemplateResult {
    const isAddedProductVisible = this.showAddedToCartProduct && !!this.connectProduct;
    const isContentVisible = this.showContentTitleAndDescription && !!(this.contentTitle || this.contentDescription);

    return html`
      <div id="upsell-modal-container">
        ${isAddedProductVisible
          ? html` <div class="mb-4">
              <mhecomm-sharpen-upsell-modal-cart-product .product="${this.connectProduct}">
              </mhecomm-sharpen-upsell-modal-cart-product>
            </div>`
          : nothing}
        ${isContentVisible
          ? html`
              <div class="mb-2">
                ${this.contentTitle
                  ? html` <h3
                      id="container-title"
                      class="h5 fw-bold mb-1"
                      aria-live="polite"
                      aria-atomic="true"
                      aria-label="${this.contentTitle}">
                      ${this.contentTitle}
                    </h3>`
                  : nothing}
                ${this.contentDescription ? html`<div><span>${this.contentDescription}</span></div>` : nothing}
              </div>
            `
          : nothing}
        <div
          id="sharpen-container-size"
          class="${isAddedProductVisible || isContentVisible ? 'border rounded-2 p-3 shadow' : ''}">
          <div class="d-flex flex-column flex-sm-row align-items-start gap-2">
            <div
              class="sharpen-product-img-border flex-shrink-0 bg-light d-flex justify-content-center justify-content-sm-start align-self-center"
              style="width: 100px;">
              <img
                class="sharpen-product-img mw-100"
                src="${getImageSrcUrl(this.sharpenSearchServiceProductData?.id || '')}"
                alt="" />
            </div>
            <div class="flex-grow-1">
              <h3 class="h6 fw-bold mb-0">${this.sharpenOfferTitle}</h3>
              <p class="m-0 pb-2">${this.sharpenOfferDescription}</p>
            </div>
          </div>
          <div class="item row">
            <mhecomm-sharpen-legal-disclaimer ?isSharpenPlus=${true}></mhecomm-sharpen-legal-disclaimer>
            ${this.sharpenProduct
              ? html` <div class="col">
                  <span class="fw-bold">$${parseFloat(this.sharpenProduct.netPrice).toFixed(2)}</span>
                  (${this.sharpenProduct.duration + '-' + this.sharpenProduct.durationPeriod + 's Access'})
                </div>`
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Closing the modal using the cross icon triggers an analytics event.
   */
  private onClose(): void {
    this.modalActionAnalytics('click', 'decline-sharpen', 'Sharpen upsell close');
  }

  /**
   * Closing the modal using the secondary button triggers an analytics event, then redirects to the checkout.
   */
  private onDecline(): void {
    this.modalActionAnalytics('click', 'decline-sharpen', 'Sharpen upsell decline');
    this.doRedirect(store.getState() as StoreState);
  }

  /**
   * Confirming the modal triggers the add-to-cart action for the Sharpen offer.
   * Depending on the result, it either shows an error toast or redirects to the checkout.
   */
  private onConfirm(): void {
    if (!this.sharpenProduct) {
      this.addToCartFailed = true;
      return;
    }

    this.modal?.setPrimaryButton({ ...this.primaryButton!, isLoading: true });
    this.modal?.setSecondaryButton({ ...this.secondaryButton!, isDisabled: true });

    store
      .dispatch(
        addToCart(
          addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
            quantity: 1,
            businessUnit: BusinessUnit.HE,
            isbn: this.sharpenProduct?.isbn,
            isbn13: this.sharpenProduct?.isbn13,
            freeTrial: false,
            offerPricingId: this.sharpenProduct?.offerPricingId,
          })
        )
      )
      .then(this.handleResponse.bind(this));
  }

  private async handleResponse(): Promise<void> {
    await waitForCart();
    const state = store.getState() as StoreState;

    this.modal?.setPrimaryButton({ ...this.primaryButton!, isLoading: false });
    this.modal?.setSecondaryButton({ ...this.secondaryButton!, isDisabled: false });

    if (getCartStatus(state) === ActionStatus.failed) {
      this.addToCartFailed = true;
      return;
    }

    this.modalActionAnalytics('added-to-cart', 'added-to-cart');
    this.doRedirect(state);
  }

  private modalActionAnalytics(name: string, eventName: string, label?: string): void {
    saveCartLocalStorageObject({
      programSourceId: this.sharpenSearchServiceProductData?.id || '',
      capturePDPLocalStorage: true,
      isbn10: this.sharpenProduct?.isbn || '',
      isbn13: this.sharpenProduct?.isbn13 || '',
    });

    SharpenUpsellAnalytics.trackUpsellProduct({
      element: this,
      name: name,
      eventName: eventName,
      id: this.id,
      businessUnit: BusinessUnit.HE,
      programId: this.sharpenSearchServiceProductData?.id || '',
      upsellProduct: this.sharpenProduct!,
      storeCategory: this.store,
      additionalData: {
        ...(label && { label }),
        orderType: OrderType.HE_CONNECT,
        orderFlow: AnalyticsOrderFlowTypes.HE_CONNECT_SHARPEN,
      },
    });
  }

  private doRedirect(state: StoreState): void {
    window.location.href = getCheckoutUrl(state, this.store);
  }

  /**
   * Maps and selects the offer with the shortest duration from the Sharpen search service.
   *
   * @param state
   */
  private mapSharpenProductData(state: StoreState): void {
    this.sharpenSearchServiceProductData = getSharpenSearchServiceProductData(state);
    const sortedOffersByDuration = getSortedSharpenOffersByDuration(this.sharpenSearchServiceProductData?.scdfs);

    if (sortedOffersByDuration) {
      this.sharpenProduct = sortedOffersByDuration[0];
      store.dispatch(setSelectedSharpenUpsellData(this.sharpenProduct));

      SharpenUpsellAnalytics.viewFrequentlyBoughWith({
        element: this,
        pageType: SharpenPageType.TUNNEL_CART,
        upsellProduct: this.sharpenProduct!,
        additionalData: {
          label: this.modalHeader,
          orderType: OrderType.HE_CONNECT,
          orderFlow: AnalyticsOrderFlowTypes.HE_CONNECT_SHARPEN,
        },
      });
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-sharpen-upsell-modal': SharpenUpsellModal;
  }
}
